import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/**
 * Post-processing chain: render -> bloom -> output (tone mapping + sRGB).
 * Bloom only lifts authored emissives past the threshold; it is the finishing
 * pass for the neon language, never the source of detail.
 */
export class RenderPipeline {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    bloomStrength: number,
  ) {
    // The composer issues several internal renders per frame; accumulate
    // renderer.info across them so diagnostics stay meaningful.
    renderer.info.autoReset = false;
    this.renderer = renderer;
    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(scene, camera));
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(renderer.domElement.clientWidth, renderer.domElement.clientHeight),
      bloomStrength,
      0.32, // radius
      0.72, // threshold: only authored emissives + sun bloom
    );
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
  }

  setBloom(strength: number): void {
    this.bloom.strength = strength;
  }

  setSize(width: number, height: number, dpr: number): void {
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(width, height);
  }

  render(): void {
    this.renderer.info.reset();
    this.composer.render();
  }

  dispose(): void {
    this.composer.dispose();
  }
}
